import { Scene } from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { FBXLoader } from "three/addons/loaders/FBXLoader.js";
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'meshoptimizer';
import { VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm';
import { VRMAnimationLoaderPlugin } from '@pixiv/three-vrm-animation';
import EventEmitter from 'events';
import Root from "../../app";
import Renderer from "../renderer/renderer";
import { defaultLoaderConfig } from '../../config/default.config';
import type {
  LoaderOption,
  ResourceType,
  LoadedResource,
  LoadedFile,
  SupportedFormat,
  LoaderInstance,
  LoaderProgressEvent,
  ProgressEvent,
  TotalProgressEvent
} from './type';
import {
  extractDataFromResource,
  normalizeLoadedFile,
} from './core';
import {
  validateResources,
  calculateProgress,
  calculateTotalProgress
} from './util';

export default class Loader extends EventEmitter {
  gltfLoader!: GLTFLoader;
  fbxLoader!: FBXLoader;
  vrmLoader?: LoaderInstance;
  vrmaLoader?: LoaderInstance;
  ktx2Loader?: KTX2Loader;

  scene!: Scene;
  root!: Root;
  renderer!: Renderer;
  options: LoaderOption;
  resources: { [name: string]: LoadedResource };

  progress: number;
  resourcesToLoad: number;
  progressThrottle: number;
  lastProgressTime: number;

  constructor(options?: Partial<LoaderOption>) {
    super();

    this.options = { ...defaultLoaderConfig.options, ...options };
    this.resources = {};
    this.progress = 0;
    this.resourcesToLoad = 0;
    this.progressThrottle = defaultLoaderConfig.progress.throttle;
    this.lastProgressTime = 0;
  }

  setRoot(root: Root) {
    this.root = root;
    this.scene = this.root.scene;
    this.renderer = this.root.renderer;
    this.setLoaders();
  }

  setLoaders() {
    this.setupGLTFLoader();
    this.setupFBXLoader();
    this.setupVRMLoader();
    this.setupVRMALoader();
  }

  private setupGLTFLoader() {
    this.gltfLoader = new GLTFLoader();

    if (this.options.isKTX2) {
      this.ktx2Loader = new KTX2Loader().setTranscoderPath(defaultLoaderConfig.paths.ktx2);
      this.ktx2Loader.detectSupport(this.renderer.instance);
      this.gltfLoader.setKTX2Loader(this.ktx2Loader);
    }

    if (this.options.isDRACO) {
      const dracoLoader = new DRACOLoader().setDecoderPath(defaultLoaderConfig.paths.draco);
      this.gltfLoader.setDRACOLoader(dracoLoader);
    }

    if (this.options.isMeshopt) {
      this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);
    }
  }

  private setupFBXLoader() {
    this.fbxLoader = new FBXLoader();
  }

  private setupVRMLoader() {

    // 기존 VRM 로더 정리
    this.vrmLoader = undefined;

    if (this.options.isVRM) {
      // VRM 로더 설정 - GLTFLoader에 VRMLoaderPlugin 등록
      this.gltfLoader.register((parser) => {
        return new VRMLoaderPlugin(parser);
      });

      this.vrmLoader = {
        load: (url: string, onLoad: (result: LoadedFile) => void, onProgress?: (event: LoaderProgressEvent) => void, onError?: (error: Error) => void) => {
          this.gltfLoader.load(
            url,
            (gltf) => {
              // VRM 데이터는 gltf.userData.vrm에 저장됨
              const vrm = gltf.userData.vrm;
              const vrmData = {
                scene: vrm?.scene || gltf.scene,
                animations: gltf.animations || [],
                userData: { vrm }
              };
              VRMUtils.rotateVRM0(vrm);
              onLoad(vrmData as LoadedFile);
            },
            onProgress ? (event: { loaded: number; total: number }) => {
              onProgress({ loaded: event.loaded, total: event.total });
            } : undefined,
            onError ? (err: unknown) => {
              onError(err instanceof Error ? err : new Error(String(err)));
            } : undefined
          );
        }
      };
    } else {
    }
  }

  private setupVRMALoader() {

    // 기존 VRMA 로더 정리
    this.vrmaLoader = undefined;

    if (this.options.isVRM) {
      // VRMA 로더 설정 - GLTFLoader에 VRMAnimationLoaderPlugin 등록
      this.gltfLoader.register((parser) => {
        return new VRMAnimationLoaderPlugin(parser);
      });

      this.vrmaLoader = {
        load: (url: string, onLoad: (result: LoadedFile) => void, onProgress?: (event: LoaderProgressEvent) => void, onError?: (error: Error) => void) => {
          this.gltfLoader.load(
            url,
            (gltf) => {

              // VRMAnimationLoaderPlugin이 처리한 VRMAnimation 객체들 추출
              const vrmAnimations = gltf.userData.vrmAnimations;

              if (!vrmAnimations || vrmAnimations.length === 0) {
                console.warn(`⚠️ VRMA 파일에서 VRMAnimation을 찾을 수 없음: ${url}`);
                console.warn("userData 구조:", Object.keys(gltf.userData || {}));
              }


              // VRMAnimation 객체들을 userData에 저장하고, animations는 빈 배열로 설정
              // (나중에 BaseAvatarPlayer에서 VRM 모델과 함께 createVRMAnimationClip으로 변환)
              const vrmaData = {
                scene: gltf.scene,
                animations: [], // 빈 배열 - VRMAnimation은 별도 처리 필요
                userData: {
                  vrmAnimations: vrmAnimations || [],
                  originalGltf: gltf
                }
              };

              onLoad(vrmaData as LoadedFile);
            },
            onProgress ? (event: { loaded: number; total: number }) => {
              onProgress({ loaded: event.loaded, total: event.total });
            } : undefined,
            onError ? (err: unknown) => {
              onError(err instanceof Error ? err : new Error(String(err)));
            } : undefined
          );
        }
      };
    }
  }

  private getLoaderForFormat(format: SupportedFormat): LoaderInstance {
    switch (format) {
      case 'gltf':
      case 'glb':
        return {
          load: (url: string, onLoad: (result: LoadedFile) => void, onProgress?: (event: LoaderProgressEvent) => void, onError?: (error: Error) => void) => {
            this.gltfLoader.load(
              url,
              (gltf: GLTF) => onLoad(gltf as LoadedFile),
              onProgress ? (event: { loaded: number; total: number }) => {
                onProgress({ loaded: event.loaded, total: event.total });
              } : undefined,
              onError ? (err: unknown) => {
                onError(err instanceof Error ? err : new Error(String(err)));
              } : undefined
            );
          }
        };
      case 'fbx':
        return {
          load: (url: string, onLoad: (result: LoadedFile) => void, onProgress?: (event: LoaderProgressEvent) => void, onError?: (error: Error) => void) => {
            this.fbxLoader.load(
              url,
              (fbx) => onLoad(fbx as LoadedFile),
              onProgress ? (event: { loaded: number; total: number }) => {
                onProgress({ loaded: event.loaded, total: event.total });
              } : undefined,
              onError ? (err: unknown) => {
                onError(err instanceof Error ? err : new Error(String(err)));
              } : undefined
            );
          }
        };
      case 'vrm':
        if (!this.vrmLoader) {
          throw new Error('VRM loader is not enabled. Set isVRM option to true.');
        }
        return this.vrmLoader;
      case 'vrma':
        if (!this.vrmaLoader) {
          throw new Error('VRMA loader is not enabled. Set isVRM option to true.');
        }
        return this.vrmaLoader;
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  }

  private emitProgress(event: ProgressEvent) {
    if (!defaultLoaderConfig.progress.enabled) return;

    const now = Date.now();
    if (now - this.lastProgressTime < this.progressThrottle) return;

    this.lastProgressTime = now;
    this.emit('progress', event);
  }

  private emitTotalProgress(event: TotalProgressEvent) {
    if (!defaultLoaderConfig.progress.enabled) {
      return;
    }

    this.emit('totalProgress', event);
  }

  async load(resource: ResourceType): Promise<LoadedResource> {
    return new Promise((resolve, reject) => {
      const loader = this.getLoaderForFormat(resource.format);

      loader.load(
        resource.path,
        (loadedFile: LoadedFile) => {
          try {
            // 로딩된 파일을 표준 형태로 변환
            const normalizedFile = normalizeLoadedFile(loadedFile, resource.format);
            const processedResource = extractDataFromResource(resource, normalizedFile);
            if (processedResource.file.animations && processedResource.file.animations.length > 0) {
              processedResource.file.animations.forEach((animation, index) => {
                const newName = processedResource.file.animations?.length === 1
                  ? resource.name
                  : `${resource.name}_${index}`;
                animation.name = newName;
              });
            }

            this.resources[resource.name] = processedResource;
            this.emit('resourceLoaded', {
              name: resource.name,
              resource: processedResource
            });
            console.log('📊 Loader: 리소스 로드 완료', resource.name, processedResource.file);

            resolve(processedResource);
          } catch (error) {
            reject(error);
          }
        },
        (progress: LoaderProgressEvent) => {
          const progressEvent: ProgressEvent = {
            resource: resource.name,
            loaded: progress.loaded,
            total: progress.total,
            progress: calculateProgress(progress.loaded, progress.total)
          };

          this.emitProgress(progressEvent);
        },
        (error: Error) => {
          reject(error);
        }
      );
    });
  }

  async loadAll(resources: ResourceType[]): Promise<LoadedResource[]> {
    if (!resources.length) {
      console.log('📊 Loader: 로드할 리소스가 없음');
      return [];
    }


    // 리소스 검증
    validateResources(resources);

    this.resourcesToLoad = resources.length;
    let loadedCount = 0;

    const results = await Promise.all(
      resources.map(async (resource) => {
        const result = await this.load(resource);
        loadedCount++;

        const totalProgressEvent: TotalProgressEvent = {
          loadedCount,
          totalCount: this.resourcesToLoad,
          progress: calculateTotalProgress(loadedCount, this.resourcesToLoad)
        };
        this.emitTotalProgress(totalProgressEvent);

        return result;
      })
    );

    this.emit('allResourcesLoaded', { resources: this.resources });
    return results;
  }

  /**
   * 특정 리소스를 이름으로 가져옵니다.
   */
  getResource(name: string): LoadedResource | undefined {
    return this.resources[name];
  }

  /**
   * 로딩된 모든 리소스를 가져옵니다.
   */
  getAllResources(): { [name: string]: LoadedResource } {
    return this.resources;
  }

  /**
   * 특정 리소스의 애니메이션을 가져옵니다.
   */
  getAnimations(name: string): LoadedResource['animations'] {
    const resource = this.getResource(name);
    return resource?.animations;
  }

  /**
   * 특정 리소스의 모델을 가져옵니다.
   */
  getModel(name: string): LoadedResource['model'] {
    const resource = this.getResource(name);
    return resource?.model;
  }

  /**
   * 특정 형식의 리소스들을 필터링합니다.
   */
  getResourcesByFormat(format: SupportedFormat): LoadedResource[] {
    return Object.values(this.resources).filter(resource => resource.format === format);
  }

  /**
   * 리소스 캐시를 지웁니다.
   */
  clearResources() {
    this.resources = {};
    this.progress = 0;
    this.resourcesToLoad = 0;
  }

  /**
   * 특정 리소스를 캐시에서 제거합니다.
   */
  removeResource(name: string): boolean {
    if (this.resources[name]) {
      delete this.resources[name];
      return true;
    }
    return false;
  }

  /**
   * 로더 설정을 업데이트합니다.
   */
  updateOptions(options: Partial<LoaderOption>) {
    this.options = { ...this.options, ...options };
    this.setLoaders();
  }

  /**
   * 진행률 throttle을 설정합니다.
   */
  setProgressThrottle(throttle: number) {
    this.progressThrottle = throttle;
  }

  /**
   * 로더 인스턴스 정리
   */
  dispose() {
    this.clearResources();
    this.removeAllListeners();

    if (this.ktx2Loader) {
      this.ktx2Loader.dispose();
    }
  }
}